import { Root, Overlay } from "@kobalte/core/dialog"
import { Button } from "@opencode/ui/button"
import { Dialog, DialogBody, DialogHeader, DialogTitleGroup } from "@opencode/ui/dialog"
import { onCleanup, onMount, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"
import { useLanguage } from "@/runtime/i18n/language"
import { flushPersistedAsync } from "@/runtime/persistence/persist"
import { activateWebUpdate, watchWebUpdates } from "@/runtime/platform/web-update"
import "./web.css"

export function WebUpdates() {
  const language = useLanguage()
  const [state, setState] = createStore({ ready: false, busy: false, error: "" as "" | "other-tabs" | "failed" })
  let registration: ServiceWorkerRegistration | undefined
  onMount(() => {
    const dispose = watchWebUpdates((next) => {
      registration = next
      setState("ready", true)
    })
    onCleanup(dispose)
  })
  const update = async () => {
    if (!registration || state.busy) return
    setState({ busy: true, error: "" })
    let timeout: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        flushPersistedAsync(),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error("Saving drafts timed out")), 15_000)
        }),
      ])
      clearTimeout(timeout)
      const result = await activateWebUpdate(registration)
      if (result === "other-tabs") {
        setState({ busy: false, error: "other-tabs" })
        return
      }
      window.location.reload()
    } catch {
      setState({ busy: false, error: "failed" })
    } finally {
      clearTimeout(timeout)
    }
  }
  return (
    <>
      <Show when={state.ready && !state.busy}>
        <Portal>
          <section class="web-update" role="status" aria-label={language.t("web.update.title")}>
            <strong>{language.t("web.update.title")}</strong>
            <p>
              {language.t(
                state.error === "other-tabs"
                  ? "web.update.otherTabs"
                  : state.error === "failed"
                    ? "web.update.failed"
                    : "web.update.description",
              )}
            </p>
            <Button variant="neutral" size="small" onClick={() => void update()}>
              {language.t("web.update.action")}
            </Button>
          </section>
        </Portal>
      </Show>
      <Root open={state.busy} onOpenChange={() => {}}>
        <Portal>
          <Show when={state.busy}>
            <Overlay data-component="dialog-overlay" class="web-update-overlay" />
            <div class="web-update-dialog">
              <Dialog fit>
                <DialogHeader hideClose>
                  <DialogTitleGroup
                    title={language.t("web.update.updating")}
                    description={language.t("web.update.saving")}
                  />
                </DialogHeader>
                <DialogBody>
                  <div class="web-update-skeleton" aria-hidden="true">
                    <span />
                    <span />
                  </div>
                </DialogBody>
              </Dialog>
            </div>
          </Show>
        </Portal>
      </Root>
    </>
  )
}
